'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tree, TreeItem } from '../components/ui/tree';
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  Power,
  Folder,
  FolderOpen,
  FolderPlus,
  Building2,
  Search,
} from 'lucide-react';
import {
  getDepartmentList,
  createDepartment,
  updateDepartment,
  toggleDepartment,
  deleteDepartment,
  Department,
} from '../api/department';
import { useUserStore } from '../stores/userStore';
import { useToast } from '../components/ui/toast';

interface TreeNode extends Department {
  children: TreeNode[];
  depth: number;
}

function buildTree(list: Department[]): TreeNode[] {
  const map = new Map<number, TreeNode>();
  list.forEach((d) => map.set(d.id, { ...d, children: [], depth: 0 }));
  const roots: TreeNode[] = [];
  list.forEach((d) => {
    const node = map.get(d.id)!;
    if (d.parent_id != null && map.has(d.parent_id)) {
      map.get(d.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const assignDepth = (nodes: TreeNode[], depth: number) => {
    nodes.sort((a, b) => a.sort_order - b.sort_order);
    nodes.forEach((n) => {
      n.depth = depth;
      assignDepth(n.children, depth + 1);
    });
  };
  assignDepth(roots, 0);
  return roots;
}

function collectDescendants(list: Department[], id: number): Set<number> {
  const childrenMap = new Map<number, number[]>();
  list.forEach((d) => {
    if (d.parent_id != null) {
      const arr = childrenMap.get(d.parent_id) || [];
      arr.push(d.id);
      childrenMap.set(d.parent_id, arr);
    }
  });
  const result = new Set<number>();
  const stack = [...(childrenMap.get(id) || [])];
  while (stack.length) {
    const cur = stack.pop()!;
    if (result.has(cur)) continue;
    result.add(cur);
    stack.push(...(childrenMap.get(cur) || []));
  }
  return result;
}

export function DepartmentManagePage({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useUserStore();
  const canManage = user?.role === 'HR' || user?.role === 'CEO';

  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const { showToast } = useToast();

  // 新增/编辑弹窗
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formId, setFormId] = useState(0);
  const [formName, setFormName] = useState('');
  const [formParentId, setFormParentId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 删除确认
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getDepartmentList(true);
      setDepartments(list);
    } catch (e) {
      showToast('加载部门失败', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tree = useMemo(() => buildTree(departments), [departments]);

  const searchLower = search.trim().toLowerCase();

  const activeCount = useMemo(
    () => departments.filter((d) => d.is_active).length,
    [departments]
  );

  // 搜索时：自动展开命中部门的所有祖先，使其可见
  const autoExpanded = useMemo(() => {
    if (!searchLower) return null;
    const byId = new Map(departments.map((d) => [d.id, d] as const));
    const ids = new Set<number>();
    departments.forEach((d) => {
      if (!d.name.toLowerCase().includes(searchLower)) return;
      let cur: Department | undefined = d;
      while (cur && cur.parent_id != null) {
        ids.add(cur.parent_id);
        cur = byId.get(cur.parent_id);
      }
    });
    return ids;
  }, [departments, searchLower]);

  const effExpanded = useMemo(() => {
    if (!autoExpanded) return expanded;
    const s = new Set<number>(expanded);
    autoExpanded.forEach((id) => s.add(id));
    return s;
  }, [expanded, autoExpanded]);

  const visibleRows = useMemo(() => {
    const rows: TreeNode[] = [];
    const walk = (nodes: TreeNode[]) => {
      nodes.forEach((n) => {
        rows.push(n);
        if (n.children.length && effExpanded.has(n.id)) walk(n.children);
      });
    };
    walk(tree);
    return rows;
  }, [tree, effExpanded]);

  const selected = useMemo(
    () => departments.find((d) => d.id === selectedId) || null,
    [departments, selectedId]
  );
  const selectedParentName = useMemo(() => {
    if (!selected || selected.parent_id == null) return '顶级部门';
    return departments.find((d) => d.id === selected.parent_id)?.name || '—';
  }, [selected, departments]);
  // 完整层级路径，如「集团 / 研发中心 / 前端组」
  const selectedPath = useMemo(() => {
    if (!selected) return '';
    const byId = new Map(departments.map((d) => [d.id, d] as const));
    const names: string[] = [];
    let cur: Department | undefined = selected;
    while (cur) {
      names.unshift(cur.name);
      cur = cur.parent_id != null ? byId.get(cur.parent_id) : undefined;
    }
    return names.join(' / ');
  }, [selected, departments]);
  const selectedChildren = useMemo(
    () =>
      selected
        ? departments
            .filter((d) => d.parent_id === selected.id)
            .sort((a, b) => a.sort_order - b.sort_order)
        : [],
    [selected, departments]
  );

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = (parentId: number | null) => {
    setFormMode('create');
    setFormId(0);
    setFormName('');
    setFormParentId(parentId);
    setFormOpen(true);
    if (parentId != null) setExpanded((p) => new Set(p).add(parentId));
  };

  const openEdit = (d: Department) => {
    setFormMode('edit');
    setFormId(d.id);
    setFormName(d.name);
    setFormParentId(d.parent_id ?? null);
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    if (!formName.trim()) {
      showToast('请输入部门名称', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      if (formMode === 'create') {
        const created = await createDepartment({ name: formName.trim(), parent_id: formParentId });
        showToast('创建成功');
        setFormOpen(false);
        await loadData();
        if (created?.id) setSelectedId(created.id);
      } else {
        await updateDepartment(formId, { name: formName.trim(), parent_id: formParentId });
        showToast('保存成功');
        setFormOpen(false);
        await loadData();
      }
    } catch (e: any) {
      showToast(e.response?.data?.detail || '操作失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (d: Department) => {
    try {
      await toggleDepartment(d.id);
      showToast(d.is_active ? '禁用成功' : '启用成功');
      await loadData();
    } catch (e: any) {
      showToast(e.response?.data?.detail || '操作失败', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDepartment(deleteTarget.id);
      showToast('删除成功');
      if (selectedId === deleteTarget.id) setSelectedId(null);
      setDeleteTarget(null);
      await loadData();
    } catch (e: any) {
      showToast(e.response?.data?.detail || '删除失败', 'error');
      setDeleteTarget(null);
    }
  };

  const parentOptions = useMemo(() => {
    const exclude = formMode === 'edit' ? collectDescendants(departments, formId) : new Set<number>();
    if (formMode === 'edit') exclude.add(formId);
    return departments.filter((d) => !exclude.has(d.id));
  }, [departments, formMode, formId]);

  if (!canManage) {
    return (
      <div className="flex items-center justify-center flex-1 text-gray-500 text-sm">
        仅 HR / CEO 可管理部门
      </div>
    );
  }

  const folderIcon = (node: TreeNode) => {
    if (node.children.length > 0 && effExpanded.has(node.id)) {
      return <FolderOpen className="w-4 h-4 text-blue-500 shrink-0" />;
    }
    if (node.children.length > 0) {
      return <Folder className="w-4 h-4 text-blue-500 shrink-0" />;
    }
    return <Folder className="w-4 h-4 text-slate-300 shrink-0" />;
  };

  return (
    <div className="h-full flex flex-col">
      {/* 页面标题 */}
      {!embedded && (
        <div className="mb-4">
          <h1 className="text-xl font-semibold text-slate-900">部门管理</h1>
        </div>
      )}

      {/* 工具栏 */}
      <Card className="mb-4">
        <CardContent className="py-3">
          <div className="flex items-center gap-3 flex-nowrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="搜索部门名称"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-48 h-8 text-sm"
                autoComplete="off"
              />
            </div>

            <div className="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap">
              <span>
                共 <span className="font-medium text-slate-900">{departments.length}</span> 个部门
              </span>
              <span className="text-slate-300">|</span>
              <span>
                启用 <span className="font-medium text-slate-900">{activeCount}</span>
              </span>
            </div>

            <button
              className="ml-auto h-8 px-3 text-sm border border-slate-300 text-slate-600 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-1 whitespace-nowrap"
              onClick={() => {
                setSearch('');
                loadData();
              }}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </button>
          </div>
        </CardContent>
      </Card>

      {/* 主操作行 */}
      <div className="mb-4 flex items-center">
        <button
          className="h-8 px-4 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1 whitespace-nowrap shadow-sm shadow-black/5"
          onClick={() => openCreate(null)}
        >
          <Plus className="w-4 h-4" />
          新增顶级部门
        </button>
      </div>

      {/* 主体：左树右详情 */}
      <div className="flex-1 bg-white rounded-md border border-gray-200 flex min-h-0 overflow-hidden">
        {/* 左：组织架构树 */}
        <div className="w-72 flex-shrink-0 border-r border-gray-200 flex flex-col min-h-0">
          <div className="h-11 shrink-0 px-4 border-b border-gray-200 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">组织架构</span>
            <span className="text-xs text-gray-500">
              {searchLower ? `匹配 ${visibleRows.length}` : `${departments.length} 个`}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {loading ? (
              <div className="py-8 text-center text-gray-500 text-sm">加载中...</div>
            ) : visibleRows.length === 0 ? (
              <div className="py-8 px-4 text-center text-gray-500 text-sm">
                {searchLower ? '未找到匹配的部门' : '暂无部门数据'}
              </div>
            ) : (
              <Tree indent={18}>
                {visibleRows.map((node) => (
                  <TreeItem
                    key={node.id}
                    level={node.depth}
                    selected={selectedId === node.id}
                    isFolder={node.children.length > 0}
                    expanded={effExpanded.has(node.id)}
                    matched={!!searchLower && node.name.toLowerCase().includes(searchLower)}
                    disabled={!node.is_active}
                    icon={folderIcon(node)}
                    onClick={() => setSelectedId(node.id)}
                    onToggle={() => toggleExpand(node.id)}
                  >
                    {node.name}
                  </TreeItem>
                ))}
              </Tree>
            )}
          </div>
        </div>

        {/* 右：部门详情与操作 */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className="h-11 shrink-0 px-4 border-b border-gray-200 flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-slate-700">部门详情</span>
            {selected && (
              <div className="flex items-center gap-2">
                <button
                  className="h-7 px-3 text-xs font-medium rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-1 whitespace-nowrap"
                  onClick={() => openEdit(selected)}
                >
                  <Edit className="w-3.5 h-3.5" />
                  编辑 / 移动
                </button>
                <button
                  className="h-7 px-3 text-xs font-medium rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-1 whitespace-nowrap"
                  onClick={() => openCreate(selected.id)}
                >
                  <FolderPlus className="w-3.5 h-3.5" />
                  加子部门
                </button>
                <button
                  className={`h-7 px-3 text-xs font-medium rounded-md border transition-colors flex items-center gap-1 whitespace-nowrap ${
                    selected.is_active
                      ? 'border-amber-200 text-amber-700 hover:bg-amber-50'
                      : 'border-green-200 text-green-700 hover:bg-green-50'
                  }`}
                  onClick={() => handleToggle(selected)}
                >
                  <Power className="w-3.5 h-3.5" />
                  {selected.is_active ? '禁用' : '启用'}
                </button>
                <button
                  className="h-7 px-3 text-xs font-medium rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition-colors flex items-center gap-1 whitespace-nowrap"
                  onClick={() => setDeleteTarget(selected)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  删除
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {!selected ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-14 h-14 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center mb-3">
                  <Building2 className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-sm text-gray-500">请从左侧组织架构中选择一个部门</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* 头部 */}
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold text-slate-900 truncate">
                        {selected.name}
                      </h2>
                      {selected.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          启用
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          禁用
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate">{selectedPath}</p>
                  </div>
                </div>

                {/* 信息项 */}
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                  {[
                    { label: '上级部门', value: selectedParentName },
                    { label: '直属子部门', value: `${selectedChildren.length} 个` },
                    { label: '排序号', value: String(selected.sort_order) },
                    { label: '部门 ID', value: String(selected.id) },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-md border border-gray-200 bg-gray-50/60 px-3.5 py-3"
                    >
                      <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* 直属子部门 */}
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">直属子部门</div>
                  {selectedChildren.length === 0 ? (
                    <div className="rounded-md border border-dashed border-gray-200 py-6 text-center text-sm text-gray-500">
                      暂无子部门
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedChildren.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => {
                            setExpanded((p) => new Set(p).add(selected.id));
                            setSelectedId(c.id);
                          }}
                          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-sm transition-colors ${
                            c.is_active
                              ? 'border-gray-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              : 'border-gray-200 text-gray-400 line-through hover:bg-gray-50'
                          }`}
                        >
                          <Folder className="w-3.5 h-3.5" />
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 新增 / 编辑弹窗 */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{formMode === 'create' ? '新增部门' : '编辑部门'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                部门名称 <span className="text-red-500">*</span>
              </Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="输入部门名称"
                className="h-9"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label>上级部门</Label>
              <Select
                value={formParentId != null ? String(formParentId) : 'root'}
                onValueChange={(v) => setFormParentId(v === 'root' ? null : Number(v))}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="请选择上级部门" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">（顶级部门）</SelectItem>
                  {parentOptions.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">不选择上级部门时，该部门作为顶级部门</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              取消
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 shadow-sm shadow-black/5"
            >
              {submitting ? '保存中...' : formMode === 'create' ? '确认创建' : '保存修改'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认 */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-sm text-slate-600">
            确定要删除部门 <span className="font-medium text-slate-900">{deleteTarget?.name}</span>{' '}
            吗？若该部门下有子部门或已关联用户 / 职位，将无法删除。
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              取消
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DepartmentManagePage;
